import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { API_BASE_URL } from "../config";
import "../styles/EventPage.css";
import { FaBuilding, FaUserCircle } from "react-icons/fa";
import { toast } from "react-toastify";
import footerLogo from "../ui-assets/home/eventfork_white_logo.svg";
import shareIcon from "../ui-assets/home/share.svg";

const EventPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // API data state
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Speakers state
  const [speakers, setSpeakers] = useState([]);
  const [speakersLoading, setSpeakersLoading] = useState(false);
  const [speakersError, setSpeakersError] = useState(null);

  // Exhibitors state
  const [exhibitors, setExhibitors] = useState([]);
  const [exhibitorsLoading, setExhibitorsLoading] = useState(false);
  const [exhibitorsError, setExhibitorsError] = useState(null);

  // Sponsors state
  const [sponsors, setSponsors] = useState([]);
  const [sponsorsLoading, setSponsorsLoading] = useState(false);
  const [sponsorsError, setSponsorsError] = useState(null);

  // Guidelines state
  const [guidelinesBlocks, setGuidelinesBlocks] = useState([]);
  const [guidelinesLoading, setGuidelinesLoading] = useState(false);

  // Schedule state
  const [scheduleDays, setScheduleDays] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);

  // UI state
  const [activeTab, setActiveTab] = useState("info");
  const [activeScheduleDay, setActiveScheduleDay] = useState(0);

  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLinkCopied, setShareLinkCopied] = useState(false);

  // Fetch event data
  useEffect(() => {
    const fetchEventData = async () => {
      if (!id) {
        setError("Event ID not found in URL");
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/event/${id}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log("EVENT PAGE DATA 👉", data);
        setEventData(data);
      } catch (err) {
        setError(err.message);
        console.error("Error fetching event data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchEventData();
  }, [id]);

  // ✅ Handler for Register Now button
  const handleRegisterNow = () => {
    if (attendeeRegistrationStatus !== "open") {
      return;
    }

    navigate(`/free-event-register/event/${id}`);
  };
  // ✅ Handler for Submit a Talk button
  const handleSubmitTalk = () => {
    if (speakerRegistrationStatus !== "open") {
      return;
    }

    navigate(`/speaker-form/event/${id}`);
  };


  // Generate the current event page URL
 const eventShareUrl = `${window.location.origin}/eventpage/${id}`;

  const eventShareTitle = eventData?.metadata?.name || "Eventfork Event";

  const eventShareText = `Check out ${eventShareTitle} on Eventfork!`;

  // Copy event link
  const handleCopyEventLink = async () => {
    try {
      await navigator.clipboard.writeText(eventShareUrl);
      setShareLinkCopied(true);

      setTimeout(() => {
        setShareLinkCopied(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to copy event link:", error);
      toast.error("Unable to copy the event link.");
    }
  };

  // Share using the device's native share menu
  const handleNativeShare = async () => {
    if (!navigator.share) {
      toast.info("Native sharing is not supported in this browser.");
      return;
    }

    try {
      await navigator.share({
        title: eventShareTitle,
        text: eventShareText,
        url: eventShareUrl,
      });
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Unable to share event:", error);
      }
    }
  };


  const getAttendeeRegistrationMessage = () => {
    return (
      eventData?.metadata?.attendeeRegistrationMsg ||
      "Attendee registration is currently closed."
    );
  };

  const getSpeakerRegistrationMessage = () => {
    return (
      eventData?.metadata?.speakerRegistrationMsg ||
      "Speaker registration is currently closed."
    );
  };

  const formatRegistrationStartDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const getRegistrationStatus = (startDate, endDate) => {
    if (!startDate || !endDate) return "closed";

    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (now < start) {
      return "not-started";
    }

    if (now > end) {
      return "closed";
    }

    return "open";
  };



  const attendeeRegistrationStatus = getRegistrationStatus(
    eventData?.metadata?.attendees_configuration?.start_date,
    eventData?.metadata?.attendees_configuration?.end_date
  );

  const speakerRegistrationStatus = getRegistrationStatus(
    eventData?.metadata?.speaker_configuration?.start_date,
    eventData?.metadata?.speaker_configuration?.end_date
  );

  console.log("========== REGISTRATION DEBUG ==========");
  console.log(
    "ATTENDEE CONFIG:",
    eventData?.metadata?.attendees_configuration
  );
  console.log(
    "ATTENDEE START:",
    eventData?.metadata?.attendees_configuration?.register_start_date
  );
  console.log(
    "ATTENDEE END:",
    eventData?.metadata?.attendees_configuration?.register_end_date
  );

  console.log(
    "SPEAKER CONFIG:",
    eventData?.metadata?.speaker_configuration
  );
  console.log(
    "SPEAKER START:",
    eventData?.metadata?.speaker_configuration?.register_start_date
  );
  console.log(
    "SPEAKER END:",
    eventData?.metadata?.speaker_configuration?.register_end_date
  );

  console.log("ATTENDEE STATUS:", attendeeRegistrationStatus);
  console.log("SPEAKER STATUS:", speakerRegistrationStatus);

  // Fetch approved speakers
  useEffect(() => {
    if (!id) return;
    const fetchSpeakers = async () => {
      setSpeakersLoading(true);
      setSpeakersError(null);
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${API_BASE_URL}/getApprovedSpeakerDetails/${id}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        const speakersWithTopics = await Promise.all(
          data.map(async (speaker) => {
            try {
              const response = await fetch(
                `${API_BASE_URL}/getSpeakerDetailsById/${speaker.id}`
              );

              const details = await response.json();

              return {
                ...speaker,
                talkTopic:
                  details?.talk_details?.["Talk Topic"] ||
                  details?.talk_details?.["Talk Title"] ||
                  "",
              };
            } catch {
              return {
                ...speaker,
                talkTopic: "",
              };
            }
          })
        );

        setSpeakers(speakersWithTopics);
      } catch (err) {
        console.error("Error fetching speakers:", err);
        setSpeakersError("Failed to load speakers.");
        setSpeakers([]);
      } finally {
        setSpeakersLoading(false);
      }
    };
    fetchSpeakers();
  }, [id]);

  // Fetch exhibitors
  useEffect(() => {
    if (!id) return;

    const fetchExhibitors = async () => {
      setExhibitorsLoading(true);
      setExhibitorsError(null);

      try {
        const response = await fetch(
          `${API_BASE_URL}/get_exibitors/${id}`,
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setExhibitors(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching exhibitors:", err);
        setExhibitorsError("Failed to load exhibitors.");
        setExhibitors([]);
      } finally {
        setExhibitorsLoading(false);
      }
    };

    fetchExhibitors();
  }, [id]);

  // Fetch sponsors
  useEffect(() => {
    if (!id) return;

    const fetchSponsors = async () => {
      setSponsorsLoading(true);
      setSponsorsError(null);

      try {
        const response = await fetch(
          `${API_BASE_URL}/get_sponsers/${id}`,
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setSponsors(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching sponsors:", err);
        setSponsorsError("Failed to load sponsors.");
        setSponsors([]);
      } finally {
        setSponsorsLoading(false);
      }
    };

    fetchSponsors();
  }, [id]);

  // Fetch guidelines
  useEffect(() => {
    if (!id) return;
    const fetchGuidelines = async () => {
      setGuidelinesLoading(true);
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${API_BASE_URL}/event_guidelines/${id}`,
          {
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          },
        );
        if (response.status === 404) {
          setGuidelinesBlocks([]);
          return;
        }
        if (!response.ok) {
          setGuidelinesBlocks([]);
          return;
        }
        const data = await response.json();
        console.log("Guidelines Response:", data);
        let blocks = [];

        if (data?.guidelines) {
          try {
            const parsed = JSON.parse(data.guidelines);
            blocks = parsed.blocks || [];
          } catch (e) {
            console.error("JSON parse error:", e);
          }
        }
        setGuidelinesBlocks(Array.isArray(blocks) ? blocks : []);
      } catch (err) {
        console.error("Error fetching guidelines:", err);
        setGuidelinesBlocks([]);
      } finally {
        setGuidelinesLoading(false);
      }
    };
    fetchGuidelines();
  }, [id]);

  // Fetch schedule
  useEffect(() => {
    if (!id) return;
    const fetchSchedule = async () => {
      setScheduleLoading(true);
      setScheduleError(null);
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${API_BASE_URL}/event_scheduler/${id}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );
        if (response.status === 404) {
          setScheduleDays([]);
          return;
        }
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        const rawDays = data?.scheduleData?.day;
        setScheduleDays(Array.isArray(rawDays) ? rawDays : []);
      } catch (err) {
        console.error("Error fetching schedule:", err);
        setScheduleError("Failed to load schedule.");
        setScheduleDays([]);
      } finally {
        setScheduleLoading(false);
      }
    };
    fetchSchedule();
  }, [id]);

  // Sync tab from URL hash
  useEffect(() => {
    const hash = window.location.hash.substring(1);
    const validTabs = [
      "info",
      "speakers",
      "exhibitors",
      "schedule",
      "sponsors",
    ];
    if (hash && validTabs.includes(hash)) {
      setActiveTab(hash);
    }
  }, []);

  useEffect(() => {
    if (activeTab) {
      window.location.hash = activeTab;
    }
  }, [activeTab]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "";

    const datePart = dateString.split("T")[0];
    const [year, month, day] = datePart.split("-");

    const date = new Date(
      Number(year),
      Number(month) - 1,
      Number(day)
    );

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };
  const formatTime = (dateString) => {
    if (!dateString) return "";

    const timePart = dateString.split("T")[1];

    if (!timePart) return "";

    const [hours, minutes] = timePart.split(":");

    const hour = Number(hours);

    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;

    return `${String(displayHour).padStart(2, "0")}:${minutes} ${period}`;
  };
const formatScheduleTime = (value) => {
  if (!value) return "";

  // Schedule times saved as HH:mm
  const match = String(value).match(/^(\d{1,2}):(\d{2})/);

  if (match) {
    const hours = Number(match[1]);
    const minutes = Number(match[2]);

    if (hours > 23 || minutes > 59) return value;

    const period = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;

    return `${displayHours}:${String(minutes).padStart(2, "0")} ${period}`;
  }

  // ISO timestamps: display UTC without local-time conversion.
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "UTC",
  });
};

  const formatDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return "";

    const startDatePart = startDate.split("T")[0];
    const endDatePart = endDate.split("T")[0];

    const [startYear, startMonth, startDay] = startDatePart.split("-");
    const [endYear, endMonth, endDay] = endDatePart.split("-");

    const start = new Date(
      Number(startYear),
      Number(startMonth) - 1,
      Number(startDay)
    );

    const end = new Date(
      Number(endYear),
      Number(endMonth) - 1,
      Number(endDay)
    );

    if (
      start.getMonth() === end.getMonth() &&
      start.getFullYear() === end.getFullYear()
    ) {
      return `${start.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
      })}-${end.getDate()}, ${end.getFullYear()}`;
    }

    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };



  const handleQuickLinkClick = (e, tabId) => {
    e.preventDefault();
    setActiveTab(tabId);
    const section = document.getElementById(`${tabId}Section`);
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Add this helper function - put it after handleQuickLinkClick function
  const renderSocialLinks = () => {
    if (!eventData) return null;

    const socialLinks = [
      {
        key: 'twitter',
        icon: 'fab fa-twitter',
        url: eventData?.metadata?.organizationDetails?.twitter_link,
        label: 'Twitter'
      },
      {
        key: 'linkedin',
        icon: 'fab fa-linkedin-in',
        url: eventData?.metadata?.organizationDetails?.linkedin_link,
        label: 'LinkedIn'
      },
      {
        key: 'github',
        icon: 'fab fa-github',
        url: eventData?.metadata?.organizationDetails?.github_link,
        label: 'GitHub'
      },
      {
        key: 'instagram',
        icon: 'fab fa-instagram',
        url: eventData?.metadata?.organizationDetails?.instagram_link,
        label: 'Instagram'
      },
      {
        key: 'facebook',
        icon: 'fab fa-facebook-f',
        url: eventData?.metadata?.organizationDetails?.facebook_link,
        label: 'Facebook'
      },
      {
        key: 'website',
        icon: 'fas fa-globe',
        url: eventData?.metadata?.organizationDetails?.website_link,
        label: 'Website'
      }
    ];

    const hasAnySocial = socialLinks.some(
      link => link.url && link.url.trim() !== ""
    );

    // here is code for if api provide image display that image otherwise display default image






    if (!hasAnySocial) {
      return <p style={{ color: "#b0b7c3", fontSize: "0.9rem" }}>No social media links available</p>;
    }

    return (
      <div className="social-links">
        {socialLinks.map(({ key, icon, url, label }) => (
          url && url.trim() !== "" && (
            <a
              key={key}
              href={url}
              className="social-link"
              target="_blank"
              rel="noopener noreferrer"
              title={`Follow us on ${label}`}
            >
              <i className={icon} />
            </a>
          )
        ))}
      </div>
    );
  };

  // Loading state with skeleton UI
  if (loading) {
    return (
      <div className="event-page-root">
        {/* Header Skeleton */}
        <div className="skeleton-header">
          <div className="container">
            <div className="header-content">
              <div className="hero-left">
                <div style={{ display: "flex", alignItems: "center", gap: "15px", marginBottom: "30px" }}>
                  <div className="skeleton" style={{ width: "60px", height: "60px", borderRadius: "12px" }}></div>
                  <div className="skeleton" style={{ width: "400px", height: "48px", borderRadius: "8px" }}></div>
                </div>

                <div className="event-meta-bar">
                  <div className="meta-item">
                    <div className="skeleton" style={{ width: "40px", height: "40px", borderRadius: "50%" }}></div>
                    <div className="meta-text">
                      <div className="skeleton" style={{ width: "60px", height: "14px", marginBottom: "8px", borderRadius: "4px" }}></div>
                      <div className="skeleton" style={{ width: "150px", height: "20px", borderRadius: "4px" }}></div>
                    </div>
                  </div>
                  <div className="meta-item">
                    <div className="skeleton" style={{ width: "40px", height: "40px", borderRadius: "50%" }}></div>
                    <div className="meta-text">
                      <div className="skeleton" style={{ width: "60px", height: "14px", marginBottom: "8px", borderRadius: "4px" }}></div>
                      <div className="skeleton" style={{ width: "150px", height: "20px", borderRadius: "4px" }}></div>
                    </div>
                  </div>
                  <div className="meta-item">
                    <div className="skeleton" style={{ width: "40px", height: "40px", borderRadius: "50%" }}></div>
                    <div className="meta-text">
                      <div className="skeleton" style={{ width: "60px", height: "14px", marginBottom: "8px", borderRadius: "4px" }}></div>
                      <div className="skeleton" style={{ width: "150px", height: "20px", borderRadius: "4px" }}></div>
                    </div>
                  </div>
                </div>

                <div className="action-buttons">
                  <div className="skeleton" style={{ width: "180px", height: "48px", borderRadius: "8px" }}></div>
                  <div className="skeleton" style={{ width: "180px", height: "48px", borderRadius: "8px" }}></div>
                </div>
              </div>

              <div className="hero-right">
                <div style={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
                  <div className="skeleton" style={{ width: "40px", height: "40px", borderRadius: "50%" }}></div>
                  <div className="skeleton" style={{ width: "40px", height: "40px", borderRadius: "50%" }}></div>
                  <div className="skeleton" style={{ width: "40px", height: "40px", borderRadius: "50%" }}></div>
                </div>
                <div className="skeleton" style={{ width: "250px", height: "60px", marginBottom: "20px", borderRadius: "8px" }}></div>
                <div className="skeleton" style={{ width: "100%", height: "200px", borderRadius: "12px" }}></div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Skeleton */}
        <nav className="page-nav">
          <div className="container">
            <div className="nav-container">
              <div className="skeleton" style={{ width: "120px", height: "48px", borderRadius: "8px" }}></div>
              <div className="skeleton" style={{ width: "120px", height: "48px", borderRadius: "8px" }}></div>
              <div className="skeleton" style={{ width: "120px", height: "48px", borderRadius: "8px" }}></div>
              <div className="skeleton" style={{ width: "120px", height: "48px", borderRadius: "8px" }}></div>
              <div className="skeleton" style={{ width: "120px", height: "48px", borderRadius: "8px" }}></div>
            </div>
          </div>
        </nav>

        {/* Main Content Skeleton */}
        <div className="scroll-section">
          <main className="main-content">
            <div className="container">



              {/* About Section Skeleton */}
              <div className="content-section active">
                <div className="skeleton" style={{ width: "250px", height: "36px", margin: "40px 0 20px 0", borderRadius: "5px" }}></div>
                <div className="skeleton" style={{ width: "100%", height: "24px", margin: "12px 0", borderRadius: "4px" }}></div>
                <div className="skeleton" style={{ width: "90%", height: "18px", margin: "10px 0", borderRadius: "4px" }}></div>
                <div className="skeleton" style={{ width: "95%", height: "18px", margin: "10px 0", borderRadius: "4px" }}></div>
                <div className="skeleton" style={{ width: "85%", height: "18px", margin: "10px 0", borderRadius: "4px" }}></div>

                <div className="skeleton" style={{ width: "200px", height: "32px", margin: "40px 0 20px 0", borderRadius: "5px" }}></div>
                <div className="skeleton" style={{ width: "100%", height: "20px", margin: "12px 0", borderRadius: "4px" }}></div>
                <div className="skeleton" style={{ width: "90%", height: "16px", margin: "10px 0", borderRadius: "4px" }}></div>
                <div className="skeleton" style={{ width: "80%", height: "16px", margin: "10px 0", borderRadius: "4px" }}></div>
              </div>

              {/* Speakers Grid Skeleton */}
              <div style={{ marginTop: "60px" }}>
                <div className="skeleton" style={{ width: "250px", height: "36px", marginBottom: "30px", borderRadius: "8px" }}></div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: "24px" }}>
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} style={{ background: "white", padding: "20px", borderRadius: "12px", textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
                      <div className="skeleton" style={{ width: "120px", height: "120px", borderRadius: "50%", margin: "0 auto 15px" }}></div>
                      <div className="skeleton" style={{ width: "70%", height: "20px", margin: "10px auto", borderRadius: "4px" }}></div>
                      <div className="skeleton" style={{ width: "50%", height: "16px", margin: "0 auto", borderRadius: "4px" }}></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </main>
        </div>

        <style>{`
        @keyframes skeleton-loading {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }

        .skeleton {
          background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s ease-in-out infinite;
        }

        .skeleton-header {
          background: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3));
          padding: 60px 0;
          min-height: 500px;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .header-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 40px;
          align-items: start;
        }

        .hero-left, .hero-right {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .event-meta-bar {
          display: flex;
          gap: 30px;
          flex-wrap: wrap;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .meta-text {
          display: flex;
          flex-direction: column;
        }

        .action-buttons {
          display: flex;
          gap: 15px;
          margin-top: 10px;
        }

        .page-nav {
          background: white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .nav-container {
          display: flex;
          gap: 20px;
          overflow-x: auto;
          padding: 12px 0;
        }

        .content-section {
          display: block;
        }

        .scroll-section {
          background: #f8fafc;
        }

        @media (max-width: 768px) {
          .header-content {
            grid-template-columns: 1fr;
            gap: 30px;
          }

          .event-meta-bar {
            flex-direction: column;
            gap: 15px;
          }

          .action-buttons {
            flex-direction: column;
          }

          .skeleton[style*="width: 400px"] {
            width: 250px !important;
          }

          .skeleton[style*="width: 120px"] {
            width: 80px !important;
          }
        }
      `}</style>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="event-page-root">
        <div style={{ textAlign: "center", padding: "80px 20px" }}>
          <div
            style={{
              fontSize: "1.3rem",
              color: "#f56565",
              marginBottom: "15px",
            }}
          >
            <i className="fas fa-exclamation-triangle"></i> Error loading event
          </div>
          <p style={{ color: "#718096" }}>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="btn btn-primary"
            style={{ marginTop: "15px" }}
          >
            <i className="fas fa-redo"></i> Try Again
          </button>
        </div>
      </div>
    );
  }

  // Derive display values from API data
  const eventName = eventData?.metadata?.name;
  const eventDescription = eventData?.metadata?.description;
  const venue = eventData?.metadata?.venue;
  const city = eventData?.metadata?.city;
  const startDate = eventData?.metadata?.start_date;
  const endDate = eventData?.metadata?.end_date;

  // Organizer name from API
  const organizationName =
    eventData?.metadata?.organizationDetails?.organization_name;


  const bannerImage =
    eventData?.banner_file?.base64 &&
      eventData.banner_file.base64 !== "data:image/jpeg;base64,undefined"
      ? eventData.banner_file.base64
      : "/eventfork_banner.jpg";



  const logoImage =
    eventData?.logo_file?.base64 &&
      !eventData.logo_file.base64.includes("base64,null")
      ? eventData.logo_file.base64
      : "/eventfork_icon.svg";


  const eventType = eventData?.metadata?.event_type;

  // Normalize sponsor category names
  const normalizeCategory = (category) => {
    if (!category) return "Other";

    const map = {
      Dimond: "Diamond Sponsors",
      Platinum: "Platinum Sponsors",
      Gold: "Gold Sponsors",
      Silver: "Silver Sponsors",
      Bronze: "Bronze Sponsors",
      Supporting: "Supporting Partners",
      Community: "Community Partners",
    };

    return map[category] || category;
  };

  // Group sponsors by category
  const groupedSponsors = sponsors.reduce((acc, sponsor) => {
    const category = normalizeCategory(sponsor?.category);
    if (!acc[category]) acc[category] = [];
    acc[category].push(sponsor);
    return acc;
  }, {});

  return (
    <div className="event-page-root">
      {/* Header */}
      <div
        className="event-header"
        style={{
          backgroundImage: `url("${bannerImage || "/eventfork_banner.jpg"}")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}

      >
      </div>


      {/* Floating Event Summary Card */}
      <section className="event-summary-card">

       {/* Share Button - Top Right Corner */}
<button
  type="button"
  className="event-summary-share-btn"
  onClick={() => setShowShareModal(true)}
  aria-label="Share event"
  title="Share event"
>
  <img src={shareIcon} alt="" />
</button>

        <div className="event-summary-logo-column">
          {logoImage && (
            <img
              src={logoImage}
              alt={`${eventName} logo`}
              className="event-summary-logo"
            />
          )}
        </div>

        <div className="event-summary-left">
          <div className="event-summary-heading">
            <div className="event-summary-title">
              <h2>{eventName}</h2>

              {/* Organizer Name */}
              {organizationName && (
                <div className="event-organizer">
                  <i className="fas fa-university organizer-icon" />

                  <span className="organizer-label">
                    Organized by
                  </span>

                  <span className="organizer-name">
                    {organizationName}
                  </span>
                </div>
              )}


            </div>
          </div>

          <div className="event-summary-meta">
            {/* PASTE THE COMPLETE event-meta-bar BLOCK FROM STEP 4 HERE */}
            <div className="event-meta-bar">
              <div className="meta-item">
                <div className="meta-icon">
                  <i className="fas fa-map-marker-alt" />
                </div>
                <div className="meta-text">
                  <div className="meta-label">Venue</div>
                  <div className="meta-value">
                    {venue}, {city}
                  </div>
                </div>
              </div>

              <div className="meta-item">
                <div className="meta-icon">
                  <i className="fas fa-calendar-alt" />
                </div>

                <div className="meta-text">
                  <div className="meta-label">Date</div>

                  <div className="meta-value">
                    {startDate && endDate
                      ? formatDateRange(startDate, endDate)
                      : "March 15-16, 2025"}
                  </div>

                  {startDate && endDate && (
                    <div className="meta-time">
                      {formatTime(startDate)} – {formatTime(endDate)}
                    </div>
                  )}
                </div>
              </div>

              <div className="meta-item">
                <div className="meta-icon">
                  <i className="fas fa-tag" />
                </div>
                <div className="meta-text">
                  <div className="meta-label">Type</div>
                  <div className="meta-value">{eventType}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="event-summary-actions">
          {/* PASTE THE COMPLETE action-buttons BLOCK FROM STEP 5 HERE */}
          <div
            className={`action-buttons ${!eventData?.metadata?.isAttendeeRegistrationOpened ||
              !eventData?.metadata?.isSpeakerRegistrationOpened
              ? "single-column"
              : ""
              }`}
          >
            {/* ✅ Updated Register Now button */}
            <div className="registration-button-wrapper">
              <button
                className={`btn ${attendeeRegistrationStatus === "open"
                  ? "btn-primary"
                  : "btn-register-disabled"
                  } ${attendeeRegistrationStatus === "not-started"
                    ? "registration-not-started"
                    : ""
                  }`}
                onClick={handleRegisterNow}
                disabled={attendeeRegistrationStatus !== "open"}
              >
                <i className="fas fa-ticket-alt" />

                {attendeeRegistrationStatus === "open" ? (
                  "Register Now"
                ) : attendeeRegistrationStatus === "not-started" ? (
                  <span className="registration-start-text">
                    <span>Registration Starts on</span>
                    <span>
                      {formatRegistrationStartDate(
                        eventData?.metadata?.attendees_configuration?.start_date
                      )}
                    </span>
                  </span>
                ) : (
                  "Registration Closed"
                )}
              </button>

              {attendeeRegistrationStatus !== "open" && (
                <div className="registration-tooltip">
                  {attendeeRegistrationStatus === "not-started"
                    ? "Attendee registration is yet to start."
                    : "Attendee registration is closed, registration is over."}
                </div>
              )}
            </div>

            {/* ✅ Updated Submit a Talk button */}
            <div className="registration-button-wrapper">
              <button
                className={`btn ${speakerRegistrationStatus === "open"
                  ? "btn-primary"
                  : "btn-speaker-disabled"
                  } ${speakerRegistrationStatus === "not-started"
                    ? "registration-not-started"
                    : ""
                  }`}
                onClick={handleSubmitTalk}
                disabled={speakerRegistrationStatus !== "open"}
              >
                <i className="fas fa-microphone" />

                {speakerRegistrationStatus === "open" ? (
                  "Submit a Talk"
                ) : speakerRegistrationStatus === "not-started" ? (
                  <span className="registration-start-text">
                    <span>Speaker Registration Starts on</span>
                    <span>
                      {formatRegistrationStartDate(
                        eventData?.metadata?.speaker_configuration?.start_date
                      )}
                    </span>
                  </span>
                ) : (
                  "Talk Submission Closed"
                )}
              </button>

              {speakerRegistrationStatus !== "open" && (
                <div className="registration-tooltip">
                  {speakerRegistrationStatus === "not-started"
                    ? "Speaker registration is yet to start."
                    : "Speaker registration is closed, registration is over."}
                </div>
              )}
            </div>
          </div>

        </div>
      </section>



      {/* Navigation */}
      <nav className="page-nav">
        <div className="container">
          <div className="nav-container">
            {[
              { id: "info", icon: "fas fa-info-circle", label: "Event Info" },
              { id: "speakers", icon: "fas fa-users", label: "Speakers" },
              { id: "exhibitors", icon: "fas fa-store", label: "Exhibitors" },
              {
                id: "schedule",
                icon: "fas fa-calendar-alt",
                label: "Schedule",
              },
              { id: "sponsors", icon: "fas fa-handshake", label: "Sponsors" },
            ].map(({ id: tabId, icon, label }) => (
              <div
                key={tabId}
                className={`nav-tab ${activeTab === tabId ? "active" : ""}`}
                onClick={() => setActiveTab(tabId)}
              >
                <i className={icon}></i>
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="scroll-section">
        <main className="main-content">
          <div className="container">
            {/* Event Info Section */}
            <section
              className={`content-section ${activeTab === "info" ? "active" : ""}`}
              id="infoSection"
            >
              <h2 className="section-title">
  About the {eventType || "Event"}
</h2>
              <div className="event-description">
                <div className="description-text">
                  <p>{eventDescription}</p>
                </div>
              </div>

              <h2 className="section-title">Guidelines</h2>
              <div className="cfp-container">
                {guidelinesLoading && (
                  <div style={{ padding: "20px 0", color: "#718096" }}>
                    <i className="fas fa-spinner fa-spin" /> Loading guidelines...
                  </div>
                )}

                {!guidelinesLoading && guidelinesBlocks.length === 0 && (
                  <div style={{ padding: "20px 0", color: "#718096" }}>
                    No guidelines available for this event.
                  </div>
                )}

                {!guidelinesLoading && guidelinesBlocks.length > 0 && (
                  <div className="guidelines-content">
                    {guidelinesBlocks.map((block, idx) => {
                      switch (block.type) {
                        case "heading": {
                          const level = block.level || "H2";
                          const Tag = level.toLowerCase();
                          const fontSizes = {
                            h1: "1.6rem",
                            h2: "1.3rem",
                            h3: "1.1rem",
                          };
                          return (
                            <Tag
                              key={idx}
                              style={{
                                margin: "0px 0 8px 0",
                                fontWeight: 500,
                                color: "#1e293b",
                                fontSize: fontSizes[Tag] || "1.3rem",
                              }}
                            >
                              {block.text}
                            </Tag>
                          );
                        }
                        case "paragraph":
                          return (
                            <p
                              key={idx}
                              style={{
                                margin: "0 0 12px 0",
                                color: "#475569",
                                lineHeight: 1.7,
                                fontSize: "0.95rem",
                              }}
                            >
                              {block.text}
                            </p>
                          );
                        case "bullet_list":
                          return (
                            <ul
                              key={idx}
                              style={{
                                margin: "0 0 12px 0",
                                paddingLeft: "20px",
                              }}
                            >
                              {(block.items || [])
                                .filter((item) => item && item.trim())
                                .map((item, i) => (
                                  <li
                                    key={i}
                                    style={{
                                      color: "#475569",
                                      fontSize: "0.95rem",
                                      lineHeight: 1.7,
                                    }}
                                  >
                                    {item}
                                  </li>
                                ))}
                            </ul>
                          );
                        case "numbered_list":
                          return (
                            <ol
                              key={idx}
                              style={{
                                margin: "0 0 12px 0",
                                paddingLeft: "20px",
                              }}
                            >
                              {(block.items || [])
                                .filter((item) => item && item.trim())
                                .map((item, i) => (
                                  <li
                                    key={i}
                                    style={{
                                      color: "#475569",
                                      fontSize: "0.95rem",
                                      lineHeight: 1.7,
                                    }}
                                  >
                                    {item}
                                  </li>
                                ))}
                            </ol>
                          );
                        case "callout_info":
                          return (
                            <div
                              key={idx}
                              style={{
                                background: "#eff6ff",
                                border: "1px solid #bfdbfe",
                                borderLeft: "4px solid #3b82f6",
                                borderRadius: "8px",
                                padding: "12px 16px",
                                margin: "0 0 12px 0",
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: 600,
                                  color: "#1d4ed8",
                                  fontSize: "0.9rem",
                                  marginBottom: "4px",
                                }}
                              >
                                ℹ️ {block.title}
                              </div>
                              <div
                                style={{ color: "#1e40af", fontSize: "0.9rem" }}
                              >
                                {block.text}
                              </div>
                            </div>
                          );
                        case "callout_warning":
                          return (
                            <div
                              key={idx}
                              style={{
                                background: "#fff7ed",
                                border: "1px solid #fed7aa",
                                borderLeft: "4px solid #f97316",
                                borderRadius: "8px",
                                padding: "12px 16px",
                                margin: "0 0 12px 0",
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: 600,
                                  color: "#c2410c",
                                  fontSize: "0.9rem",
                                  marginBottom: "4px",
                                }}
                              >
                                ⚠️ {block.title}
                              </div>
                              <div
                                style={{ color: "#9a3412", fontSize: "0.9rem" }}
                              >
                                {block.text}
                              </div>
                            </div>
                          );
                        case "divider":
                          return (
                            <hr
                              key={idx}
                              style={{
                                border: "none",
                                borderTop: "1px solid #e2e8f0",
                                margin: "16px 0",
                              }}
                            />
                          );
                        default:
                          return null;
                      }
                    })}
                  </div>
                )}
              </div>
            </section>

            {/* Speakers Section */}

            <section
              className={`content-section ${activeTab === "speakers" ? "active" : ""
                }`}
              id="speakersSection"
            >
              <h2 className="section-title">Our Distinguished Speakers</h2>

              {/* Loading State */}
              {speakersLoading && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 20px",
                    color: "#718096",
                  }}
                >
                  <i
                    className="fas fa-spinner fa-spin"
                    style={{
                      fontSize: "1.5rem",
                      marginBottom: "10px",
                      display: "block",
                    }}
                  />
                  Loading speakers...
                </div>
              )}

              {/* Error State */}
              {speakersError && !speakersLoading && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 20px",
                    color: "#f56565",
                  }}
                >
                  <i className="fas fa-exclamation-circle" /> {speakersError}
                </div>
              )}

              {/* Empty State */}
              {!speakersLoading &&
                !speakersError &&
                speakers.length === 0 && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px 20px",
                      color: "#718096",
                    }}
                  >
                    <i
                      className="fas fa-microphone-slash"
                      style={{
                        fontSize: "2rem",
                        marginBottom: "12px",
                        display: "block",
                      }}
                    />
                    No speakers confirmed yet. Check back soon!
                  </div>
                )}

              {/* Speakers Grid - No Slider */}
              {!speakersLoading &&
                !speakersError &&
                speakers.length > 0 && (
                  <div className="speakers-grid">
                    {speakers.map((speaker) => (
                      <div
                        key={speaker.id}
                        className="speaker-card"
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          navigate(`/speaker-profile/${speaker.id}`)
                        }
                      >
                        <div className="speaker-header">
                          <div className="speaker-image-circle">
                            {speaker.profile_picture?.base64 ? (
                              <img
                                src={speaker.profile_picture.base64}
                                alt={speaker.name}
                                className="speaker-image"
                              />
                            ) : (
                              <div
                                className="speaker-image"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  background:
                                    "linear-gradient(135deg, #336791, #5a9fd4)",
                                  color: "#fff",
                                  fontSize: "2rem",
                                  fontWeight: 700,
                                }}
                              >
                                {speaker.name
                                  ? speaker.name.charAt(0).toUpperCase()
                                  : "S"}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="speaker-content">
                          {speaker.talkTopic && (
                            <div className="speaker-topic">
                              {speaker.talkTopic}
                            </div>
                          )}

                          <h3 className="speaker-name">
                            <FaUserCircle /> {speaker.name}
                          </h3>

                          {speaker.jobtitle && (
                            <div className="speaker-title">
                              {speaker.jobtitle}
                            </div>
                          )}

                          {speaker.company && (
                            <div className="speaker-company">
                              <FaBuilding /> {speaker.company}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
            </section>


            {/* Exhibitors Section */}
            <section
              className={`content-section ${activeTab === "exhibitors" ? "active" : ""}`}
              id="exhibitorsSection"
            >
              <h2 className="section-title">Exhibitors & Partners</h2>

              {exhibitorsLoading && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#718096",
                  }}
                >
                  <i className="fas fa-spinner fa-spin" /> Loading exhibitors...
                </div>
              )}

              {exhibitorsError && !exhibitorsLoading && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#f56565",
                  }}
                >
                  <i className="fas fa-exclamation-circle" /> {exhibitorsError}
                </div>
              )}

              {!exhibitorsLoading &&
                !exhibitorsError &&
                exhibitors.length === 0 && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px",
                      color: "#718096",
                    }}
                  >
                    No exhibitors available.
                  </div>
                )}

              {!exhibitorsLoading && exhibitors.length > 0 && (
                <div className="exhibitors-grid">
                  {exhibitors.map((exhibitor, index) => (
                    <div className="exhibitor-card" key={index}>
                      <div className="exhibitor-logo">
                        <img
                          src={exhibitor?.logo_file?.base64}
                          alt={exhibitor?.name}
                          onError={(e) => (e.target.style.display = "none")}
                        />
                      </div>
                      <h3 className="exhibitor-name">{exhibitor?.name}</h3>
                    </div>
                  ))}
                </div>
              )}
            </section>


            {/* Schedule Section */}
            <section
              className={`content-section ${activeTab === "schedule" ? "active" : ""}`}
              id="scheduleSection"
            >
              <h2 className="section-title">Event Schedule</h2>

              {scheduleLoading && (
                <div className="evsch-state">
                  <i className="fas fa-spinner fa-spin" /> Loading schedule...
                </div>
              )}

              {scheduleError && !scheduleLoading && (
                <div className="evsch-state evsch-state-error">
                  <i className="fas fa-exclamation-circle" /> {scheduleError}
                </div>
              )}

              {!scheduleLoading && !scheduleError && scheduleDays.length === 0 && (
                <div className="evsch-state">No schedule available yet.</div>
              )}

              {!scheduleLoading && scheduleDays.length > 0 && (
                <div className="evsch">
                  {/* Day tabs */}
                  <div className="evsch-tabs">
                    {scheduleDays.map((day, idx) => {
                      const dateLabel = day.start_date
                        ? new Date(day.start_date + "T00:00:00").toLocaleDateString(
                          "en-US",
                          { month: "short", day: "numeric" },
                        )
                        : "";
                      return (
                        <button
                          type="button"
                          key={idx}
                          className={`evsch-tab ${activeScheduleDay === idx ? "evsch-tab-active" : ""}`}
                          onClick={() => setActiveScheduleDay(idx)}
                        >
                          Day {idx + 1}
                          {dateLabel ? ` · ${dateLabel}` : ""}
                        </button>
                      );
                    })}
                  </div>

                  {/* Active day content */}
                  {scheduleDays.map((day, idx) => {
                    if (activeScheduleDay !== idx) return null;

                    const toMin = (t) => {
                      if (!t || typeof t !== "string" || !t.includes(":")) return 0;
                      const parts = t.split(":");
                      const h = parseInt(parts[0], 10);
                      const m = parseInt(parts[1], 10);
                      return isNaN(h) || isNaN(m) ? 0 : h * 60 + m;
                    };

                    // NOTE: `speakers` = your approved speakers array on this page
                    // (objects with id, name, jobtitle, company, profile_picture, talk_details).
                    // If it has a different name in your component, change it here.
                    const spList = Array.isArray(speakers) ? speakers : [];

                    const tasks = Array.isArray(day.scheduled_task)
                      ? [...day.scheduled_task].sort(
                        (a, b) => toMin(a.start_time) - toMin(b.start_time),
                      )
                      : [];

                    const firstStart = tasks.length ? tasks[0].start_time : "";
                    const lastEnd = tasks.length ? tasks[tasks.length - 1].end_time : "";

                    return (
                      <div className="evsch-day" key={idx}>
                        {tasks.length > 0 && (
                          <div className="evsch-summary">
                            <span>
                              <strong>{tasks.length}</strong> session
                              {tasks.length > 1 ? "s" : ""}
                             {firstStart && lastEnd
  ? ` · ${formatScheduleTime(firstStart)} – ${formatScheduleTime(lastEnd)}`
  : ""}
                            </span>
                            <span className="evsch-legend">
                              <span className="evsch-legend-item">
                                <i className="evsch-dot evsch-dot-speaker" /> Speaker
                              </span>
                              <span className="evsch-legend-item">
                                <i className="evsch-dot evsch-dot-break" /> Break
                              </span>
                            </span>
                          </div>
                        )}

                        {tasks.length === 0 ? (
                          <div className="evsch-state">
                            No sessions scheduled for this day.
                          </div>
                        ) : (
                          <div className="evsch-list">
                            {tasks.map((task, tIdx) => {
                              const activity = task.activity || {};
                              const isSpeaker = activity.type === "speaker";
                              const actName =
                                typeof activity.name === "string" ? activity.name : "Unknown";

                              const startT =
                                typeof task.start_time === "string" ? task.start_time : "";
                              const endT =
                                typeof task.end_time === "string" ? task.end_time : "";
                              const duration =
                                startT && endT && toMin(endT) > toMin(startT)
                                  ? toMin(endT) - toMin(startT)
                                  : null;

                              const room =
                                typeof task.room === "string" && task.room.trim()
                                  ? task.room.trim()
                                  : typeof activity.room === "string" && activity.room.trim()
                                    ? activity.room.trim()
                                    : "";

                              const sp = activity.speaker_id
                                ? spList.find(
                                  (s) => Number(s.id) === Number(activity.speaker_id),
                                )
                                : null;
                              const topicRaw = sp?.talk_details?.["Talk Topic"];
                              const topic =
                                typeof topicRaw === "string" ? topicRaw.trim() : "";
                              const subtitle = sp
                                ? [sp.jobtitle, sp.company].filter(Boolean).join(" · ")
                                : "";
                              const sameName = sp && sp.name === actName;
                              const initials = sp
                                ? (sp.name || "?")
                                  .split(" ")
                                  .map((w) => w[0])
                                  .join("")
                                  .toUpperCase()
                                  .slice(0, 2)
                                : "";
                              const photo = sp?.profile_picture?.base64;

                              const avatar = (size) =>
                                photo ? (
                                  <img
                                    className={`evsch-avatar evsch-avatar-${size}`}
                                    src={photo}
                                    alt={sp.name}
                                  />
                                ) : (
                                  <span
                                    className={`evsch-avatar evsch-avatar-${size} evsch-avatar-fallback`}
                                  >
                                    {initials}
                                  </span>
                                );

                              return (
                                <div
                                  key={tIdx}
                                  className={`evsch-row ${isSpeaker ? "evsch-row-speaker" : "evsch-row-break"}`}
                                >
                                  {/* Time */}
                                  <div className="evsch-time">
                                   <div className="evsch-time-range">
  {startT && endT
    ? `${formatScheduleTime(startT)} – ${formatScheduleTime(endT)}`
    : startT
      ? formatScheduleTime(startT)
      : "—"}
</div>
                                    {duration && (
                                      <div className="evsch-time-dur">{duration} min</div>
                                    )}
                                  </div>

                                  {/* Session */}
                                  <div className="evsch-main">
                                    <div className="evsch-title-line">
                                      {sameName && avatar("md")}
                                      <div className="evsch-title-wrap">
                                        <div className="evsch-title">{actName}</div>
                                        {sameName && subtitle && (
                                          <div className="evsch-sub">{subtitle}</div>
                                        )}
                                      </div>
                                    </div>

                                    {sp && !sameName && (
                                      <div className="evsch-speaker-line">
                                        {avatar("sm")}
                                        <span className="evsch-sub">
                                          {sp.name}
                                          {subtitle ? ` · ${subtitle}` : ""}
                                        </span>
                                      </div>
                                    )}

                                    {topic && (
                                      <div className="evsch-topic" title={topic}>
                                        💬 {topic}
                                      </div>
                                    )}
                                  </div>

                                  {/* Room */}
                                  <div className="evsch-room-wrap">
                                    {room && <span className="evsch-room">📍 {room}</span>}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>












            {/* Sponsors Section */}
            <section
              className={`content-section ${activeTab === "sponsors" ? "active" : ""}`}
              id="sponsorsSection"
            >
              <h2 className="section-title">Our Sponsors</h2>

              {sponsorsLoading && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#718096",
                  }}
                >
                  <i className="fas fa-spinner fa-spin" /> Loading sponsors...
                </div>
              )}

              {sponsorsError && !sponsorsLoading && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#f56565",
                  }}
                >
                  <i className="fas fa-exclamation-circle" /> {sponsorsError}
                </div>
              )}

              {!sponsorsLoading && !sponsorsError && sponsors.length === 0 && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#718096",
                  }}
                >
                  No sponsors available.
                </div>
              )}

              {!sponsorsLoading && sponsors.length > 0 && (
                <div className="sponsors-section">
                  {Object.entries(groupedSponsors).map(([category, items]) => (
                    <div className="sponsor-level" key={category}>
                      <h3 className="level-title">{category}</h3>

                      <div className="sponsors-grid">
                        {items.map((sponsor, index) => (
                          <div className="sponsor-logo" key={index}>
                            <img
                              src={sponsor?.logo_file?.base64}
                              alt={sponsor?.name}
                              onError={(e) => (e.target.style.display = "none")}
                            />
                            <p className="sponsor-name">{sponsor?.name}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </main>
      </div>



      {/* Compact Event Footer */}
      <footer className="event-page-footer">
        <div className="event-footer-grid">

          {/* Event details and logo */}
          <div className="event-footer-brand">
            {logoImage && (
              <img
                src={logoImage}
                alt={`${eventName} logo`}
                className="event-footer-event-logo"
              />
            )}

            <h2>{eventName}</h2>

            <p>
              {startDate && formatDateRange(startDate, endDate)}
              {(venue || city) && " · "}
              {[venue, city].filter(Boolean).join(", ")}
            </p>
          </div>

          {/* Quick links */}
          <div className="event-footer-quicklinks">
            <h3>Quick Links</h3>

            {[
              ["Event Info", "info"],
              ["Speakers", "speakers"],
              ["Exhibitors", "exhibitors"],
              ["Schedule", "schedule"],
              ["Sponsors", "sponsors"],
            ].map(([label, tabId]) => (
              <button
                type="button"
                key={tabId}
                onClick={() => {
                  setActiveTab(tabId);
                  document
                    .getElementById(`${tabId}Section`)
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Organizer contact and social links */}
          <div className="event-footer-contact">
            <h3>Contact Organizer</h3>

            {eventData?.metadata?.organizationDetails?.email_id && (
              <a
                href={`mailto:${eventData.metadata.organizationDetails.email_id}`}
              >
                <i className="fas fa-envelope" />
                {eventData.metadata.organizationDetails.email_id}
              </a>
            )}

            {eventData?.metadata?.organizationDetails?.contact_number && (
              <a
                href={`tel:${eventData.metadata.organizationDetails.contact_number}`}
              >
                <i className="fas fa-phone" />
                {eventData.metadata.organizationDetails.contact_number}
              </a>
            )}

            <div className="event-footer-socials">
              {renderSocialLinks()}
            </div>
          </div>
        </div>

        {/* Compact bottom bar */}
        <div className="event-footer-bottom">
          <span>
            © {new Date().getFullYear()} {eventName}. All rights reserved.
          </span>


          <a
            href="https://eventfork.com"
            target="_blank"
            rel="noopener noreferrer"
            className="event-footer-powered"
          >
            <span>Powered by</span>
            <img
              src={footerLogo}
              alt="Eventfork"
              className="event-footer-powered-logo"
            />
          </a>


        </div>
      </footer>

      {/* Event Share Modal */}
      {showShareModal && (
        <div
          className="event-share-overlay"
          onClick={() => setShowShareModal(false)}
        >
          <div
            className="event-share-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="event-share-title"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="event-share-header">
              <div>
                <h3 id="event-share-title">Share Event</h3>
                <p>Invite your friends to join this event.</p>
              </div>

              <button
                type="button"
                className="event-share-close"
                onClick={() => setShowShareModal(false)}
                aria-label="Close share popup"
              >
                &times;
              </button>
            </div>

            {/* Event Information */}
            <div className="event-share-info">
              <div className="event-share-icon">
                <i className="fas fa-calendar-check" />
              </div>

              <div className="event-share-info-text">
                <strong>{eventShareTitle}</strong>
                <span>Share this event with your friends</span>
              </div>
            </div>

            {/* Social Media Sharing */}
            <div className="event-share-social-section">
              <h4>Share via</h4>

              <div className="event-share-social-grid">
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(
                    `${eventShareText} ${eventShareUrl}`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="event-share-social-item whatsapp"
                  aria-label="Share on WhatsApp"
                >
                  <span className="event-share-social-icon">
                    <i className="fab fa-whatsapp" />
                  </span>
                  <span>WhatsApp</span>
                </a>

                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                    eventShareUrl
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="event-share-social-item linkedin"
                  aria-label="Share on LinkedIn"
                >
                  <span className="event-share-social-icon">
                    <i className="fab fa-linkedin-in" />
                  </span>
                  <span>LinkedIn</span>
                </a>

                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                    eventShareUrl
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="event-share-social-item facebook"
                  aria-label="Share on Facebook"
                >
                  <span className="event-share-social-icon">
                    <i className="fab fa-facebook-f" />
                  </span>
                  <span>Facebook</span>
                </a>

                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                    eventShareText
                  )}&url=${encodeURIComponent(eventShareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="event-share-social-item twitter"
                  aria-label="Share on X"
                >
                  <span className="event-share-social-icon">
                    <i className="fab fa-twitter" />
                  </span>
                  <span>X</span>
                </a>
              </div>
            </div>

            {/* Copy Link */}
            <div className="event-share-link-section">
              <label htmlFor="event-share-url">Event link</label>

              <div className="event-share-link-box">
                <input
                  id="event-share-url"
                  type="text"
                  value={eventShareUrl}
                  readOnly
                  onFocus={(e) => e.target.select()}
                />

                <button
                  type="button"
                  onClick={handleCopyEventLink}
                  className={`event-share-copy-btn ${shareLinkCopied ? "copied" : ""
                    }`}
                >
                  <i
                    className={`fas ${shareLinkCopied ? "fa-check" : "fa-copy"
                      }`}
                  />

                  {shareLinkCopied ? "Copied" : "Copy Link"}
                </button>
              </div>
            </div>

            {/* Native Sharing */}
            {typeof navigator !== "undefined" &&
              typeof navigator.share === "function" && (
                <button
                  type="button"
                  className="event-share-native-btn"
                  onClick={handleNativeShare}
                >
                  <i className="fas fa-share-alt" />
                  More sharing options
                </button>
              )}
          </div>
        </div>
      )}


    </div>



  );
};

export default EventPage;